import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Popover
} from '@material-ui/core';
import { ChevronDown } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import { useUnsafeTranslation } from '../../i18next';
import { toSearchTermRegexp } from '../../services/user';
import { TableSearch } from '../Table';
import { IColumn } from '../Table/VirtualizedSortable';

export const PopoverContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(0.5),
  marginBottom: p.theme.spacing(0.5),
  padding: p.theme.spacing(1)
}));

const SpacedOutRow = styled('div')<{ border?: boolean }>((p) => ({
  borderBottom: p.border ? p.theme.custom.border.standard : undefined,
  paddingBottom: p.theme.spacing(0.5),
  marginBottom: p.theme.spacing(0.5)
}));

export const ColumnSelector = <D, N extends string, O>({
  value,
  onChange,
  columns,
  requiredColumns,
  withAllOption,
  withSearch,
  checkBoxColumnKey,
  otherProps,
  label
}: {
  value: IColumn<D, N, O>[];
  onChange: (nextValue: IColumn<D, N, O>[]) => void;
  columns: IColumn<D, N, O>[];
  requiredColumns?: Set<string>;
  withAllOption?: boolean;
  withSearch?: boolean;
  checkBoxColumnKey?: string;
  otherProps?: O | undefined;
  label?: string;
}) => {
  const theme = useTheme();
  const { t } = useUnsafeTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  const selectedKeys = useMemo(() => new Set(value.map((v) => v.key)), [value]);
  const formControlLabelStyle = {
    root: css({
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`
    })
  };

  const [q, setQ] = useState('');

  const applySelection = (nextKeys: ReadonlySet<string | undefined>) => {
    const kept = value.filter((c) => nextKeys.has(c.key));
    const keptKeys = new Set(kept.map((c) => c.key));
    const added = columns.filter(
      (c) => nextKeys.has(c.key) && !keptKeys.has(c.key)
    );
    return [...kept, ...added];
  };

  const shownColumns = useMemo(() => {
    if (!q) {
      return columns;
    }
    const regexp = toSearchTermRegexp(q);
    return columns.filter(
      (c) => c.matchString && c.matchString().match(regexp)
    );
  }, [columns, q]);

  return (
    <>
      <Button
        variant="outlined"
        endIcon={<ChevronDown size={16} />}
        onClick={() => setOpen((x) => !x)}
        ref={ref}
      >
        {label ?? t('Edit columns')}
      </Button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={ref.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <PopoverContainer>
          {withSearch && (
            <SpacedOutRow>
              <TableSearch
                term={q}
                onChange={setQ}
                autoFocus
                placeholder={t('Search by name')}
              />
            </SpacedOutRow>
          )}
          {withAllOption && !q && (
            <SpacedOutRow border>
              <FormControlLabel
                classes={formControlLabelStyle}
                control={
                  <Checkbox
                    checked={selectedKeys.size >= columns.length}
                    indeterminate={
                      selectedKeys.size < columns.length &&
                      selectedKeys.size > (requiredColumns?.size || 0)
                    } // this doesn't work - and I am not sure why
                    color="primary"
                    onChange={(ev) => {
                      if (ev.target.checked) {
                        onChange(
                          applySelection(new Set(columns.map((c) => c.key)))
                        );
                      } else {
                        onChange(
                          requiredColumns
                            ? columns.filter((c) => requiredColumns.has(c.key))
                            : []
                        );
                      }
                    }}
                  />
                }
                label={t('Select all')}
              />
            </SpacedOutRow>
          )}
          <FormControl component="fieldset">
            <FormGroup>
              {shownColumns
                .filter((c) => c.key !== checkBoxColumnKey)
                .map((c) => (
                  <div key={c.key}>
                    <FormControlLabel
                      classes={formControlLabelStyle}
                      control={
                        <Checkbox
                          checked={selectedKeys.has(c.key)}
                          color="primary"
                          disabled={!!requiredColumns?.has(c.key)}
                          onChange={(ev) => {
                            const nextSet = new Set([
                              checkBoxColumnKey,
                              ...selectedKeys
                            ]);
                            if (ev.target.checked) {
                              nextSet.add(c.key);
                            } else {
                              nextSet.delete(c.key);
                            }
                            onChange(applySelection(nextSet));
                          }}
                        />
                      }
                      label={
                        c.alternateHead
                          ? c.alternateHead(otherProps)
                          : c.head(otherProps)
                      }
                    />
                  </div>
                ))}
            </FormGroup>
          </FormControl>
        </PopoverContainer>
      </Popover>
    </>
  );
};
